/**
 * التنسيق للعرض.
 *
 * الأرقام بالأرقام اللاتينية لا الهندية: النظام مالي، والقيم تُنسخ إلى
 * جداول وبرامج محاسبة لا تقرأ الأرقام الهندية.
 */

const LOCALE = 'ar-EG-u-nu-latn';

const money0 = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const money2 = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const plain = new Intl.NumberFormat(LOCALE);

export function num(value: unknown): string {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? plain.format(n) : '0';
}

/** مبلغ مالي. الكسور تُعرض عند وجودها فقط، فلا تزدحم القوائم بأصفار. */
export function money(value: unknown, currency = 'ج.م'): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return `0 ${currency}`;
  const text = Number.isInteger(n) ? money0.format(n) : money2.format(n);
  return `${text} ${currency}`;
}

/*
 * التاريخ رقميٌّ يومًا/شهرًا/سنة: 01/11/2026.
 *
 * «1 يناير 2026» يُقرأ في سطرٍ عربي «يناير 2026 1» متى جاور أرقامًا، ولا
 * يُرتَّب ولا يُنسخ إلى جدول. والرقمي هو ما يكتبه المحاسب ويقرؤه.
 */
const pad2 = (n: number) => String(n).padStart(2, '0');
const dmy = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const dateFmt = { format: dmy };
const timeFmt = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
});
const longDayFmt = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const hm = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' });
const hms = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dateTimeFmt = { format: (d: Date) => `${dmy(d)} ${hm.format(d)}` };
const dateTimeSecFmt = { format: (d: Date) => `${dmy(d)} ${hms.format(d)}` };

export function date(value: unknown): string {
  if (!value) return '—';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d);
}

export function dateTime(value: unknown): string {
  if (!value) return '—';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : dateTimeFmt.format(d);
}

/** التاريخُ والوقتُ بالثانية — حيث تهمّ اللحظةُ بالضبط، كلقطة مراقبة. */
export function dateTimeSec(value: unknown): string {
  if (!value) return '—';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : dateTimeSecFmt.format(d);
}

/** الساعة وحدها — لشبكة المواعيد حيث اليوم مكتوبٌ فوقها. */
export function time(value: unknown): string {
  if (!value) return '—';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : timeFmt.format(d);
}

/** «الثلاثاء ٣ سبتمبر» — لعنوان يومٍ يُختار منه موعد. */
export function longDay(value: unknown): string {
  if (!value) return '—';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : longDayFmt.format(d);
}

/**
 * زمن نسبي بصياغة عربية سليمة.
 *
 * Intl.RelativeTimeFormat يعطي "قبل ٣ أيام" بأرقام هندية وصياغة لا تراعي
 * المثنى، فنصوغها يدويًا.
 */
export function relative(value: unknown): string {
  if (!value) return '—';
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return '—';

  const diffMs = Date.now() - d.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);

  const minutes = Math.round(abs / 60_000);
  if (minutes < 1) return 'الآن';
  if (minutes < 60) return phrase(minutes, 'دقيقة', 'دقيقتين', 'دقائق', future);

  const hours = Math.round(minutes / 60);
  if (hours < 24) return phrase(hours, 'ساعة', 'ساعتين', 'ساعات', future);

  const days = Math.round(hours / 24);
  if (days < 30) return phrase(days, 'يوم', 'يومين', 'أيام', future);

  const months = Math.round(days / 30);
  if (months < 12) return phrase(months, 'شهر', 'شهرين', 'أشهر', future);

  const years = Math.round(months / 12);
  return phrase(years, 'سنة', 'سنتين', 'سنوات', future);
}

/**
 * صياغة العدد مع تمييزه.
 *
 * العربية تميّز المفرد والمثنى والجمع، وتعود إلى المفرد بعد العشرة:
 * "ثلاثة أيام" لكن "خمسة عشر يومًا".
 */
function phrase(n: number, one: string, two: string, many: string, future: boolean): string {
  const prefix = future ? 'خلال' : 'منذ';
  if (n === 1) return `${prefix} ${one}`;
  if (n === 2) return `${prefix} ${two}`;
  if (n <= 10) return `${prefix} ${n} ${many}`;
  return `${prefix} ${n} ${one}`;
}

/**
 * تمييز العدد بالعربية.
 *
 * القاعدة أربع حالات لا اثنتان كما في الإنجليزية:
 *   1        مفرد            نشاط واحد
 *   2        مثنى            نشاطان
 *   3 إلى 10 جمع             ثلاثة أنشطة
 *   11 فأكثر مفرد منصوب      خمسة عشر نشاطًا
 *
 * تجاهل ذلك يُنتج صياغات مثل "3 نشاطًا"، وهي أول ما يلاحظه قارئ عربي
 * ويُفقد النظام مظهره الاحترافي.
 */
export function plural(
  n: number,
  forms: { one: string; two: string; few: string; many: string },
): string {
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  if (n >= 3 && n <= 10) return `${num(n)} ${forms.few}`;
  return `${num(n)} ${forms.many}`;
}

/** صيغ الكلمات المتكررة في الواجهة. */
export const WORDS = {
  activity: { one: 'نشاط واحد', two: 'نشاطان', few: 'أنشطة', many: 'نشاطًا' },
  record: { one: 'سجل واحد', two: 'سجلان', few: 'سجلات', many: 'سجلًا' },
  account: { one: 'حساب واحد', two: 'حسابان', few: 'حسابات', many: 'حسابًا' },
  deal: { one: 'صفقة واحدة', two: 'صفقتان', few: 'صفقات', many: 'صفقة' },
  contact: { one: 'جهة اتصال واحدة', two: 'جهتا اتصال', few: 'جهات اتصال', many: 'جهة اتصال' },
  day: { one: 'يوم واحد', two: 'يومان', few: 'أيام', many: 'يومًا' },
  invoice: { one: 'فاتورة واحدة', two: 'فاتورتان', few: 'فواتير', many: 'فاتورة' },
  quotation: { one: 'عرض واحد', two: 'عرضان', few: 'عروض', many: 'عرضًا' },
  contract: { one: 'عقد واحد', two: 'عقدان', few: 'عقود', many: 'عقدًا' },
  service: { one: 'خدمة واحدة', two: 'خدمتان', few: 'خدمات', many: 'خدمة' },
  pkg: { one: 'باقة واحدة', two: 'باقتان', few: 'باقات', many: 'باقة' },
  signal: { one: 'إشارة واحدة', two: 'إشارتان', few: 'إشارات', many: 'إشارة' },
  rule: { one: 'قاعدة واحدة', two: 'قاعدتان', few: 'قواعد', many: 'قاعدة' },
  task: { one: 'مهمة واحدة', two: 'مهمتان', few: 'مهام', many: 'مهمة' },
  member: { one: 'عضو واحد', two: 'عضوان', few: 'أعضاء', many: 'عضوًا' },
  supplier: { one: 'مورد واحد', two: 'موردان', few: 'موردين', many: 'موردًا' },
  party: { one: 'طرف واحد', two: 'طرفان', few: 'أطراف', many: 'طرفًا' },
  till: { one: 'خزينة واحدة', two: 'خزينتان', few: 'خزائن', many: 'خزينة' },
  expense: { one: 'مصروف واحد', two: 'مصروفان', few: 'مصروفات', many: 'مصروفًا' },
  row: { one: 'فئة واحدة', two: 'فئتان', few: 'فئات', many: 'فئة' },
  message: { one: 'رسالة واحدة', two: 'رسالتان', few: 'رسائل', many: 'رسالة' },
  session: { one: 'جلسة واحدة', two: 'جلستان', few: 'جلسات', many: 'جلسة' },
  rep: { one: 'مندوب واحد', two: 'مندوبان', few: 'مندوبين', many: 'مندوبًا' },
  commission: { one: 'عمولة واحدة', two: 'عمولتان', few: 'عمولات', many: 'عمولة' },
  bonusLine: { one: 'بند حافز واحد', two: 'بندا حافز', few: 'بنود حوافز', many: 'بند حافز' },
  run: { one: 'كشف واحد', two: 'كشفان', few: 'كشوف', many: 'كشفًا' },
  payment: { one: 'دفعة واحدة', two: 'دفعتان', few: 'دفعات', many: 'دفعة' },
  earner: { one: 'مستحقٌّ واحد', two: 'مستحقّان', few: 'مستحقّين', many: 'مستحقًّا' },
  project: { one: 'مشروع واحد', two: 'مشروعان', few: 'مشاريع', many: 'مشروعًا' },
  section: { one: 'قسم واحد', two: 'قسمان', few: 'أقسام', many: 'قسمًا' },
  field: { one: 'حقل واحد', two: 'حقلان', few: 'حقول', many: 'حقلًا' },
  template: { one: 'قالب واحد', two: 'قالبان', few: 'قوالب', many: 'قالبًا' },
  portfolio: { one: 'محفظة واحدة', two: 'محفظتان', few: 'محافظ', many: 'محفظة' },
  job: { one: 'وظيفة واحدة', two: 'وظيفتان', few: 'وظائف', many: 'وظيفة' },
  vacancy: { one: 'شاغر واحد', two: 'شاغران', few: 'شواغر', many: 'شاغرًا' },
  candidate: { one: 'مرشّح واحد', two: 'مرشّحان', few: 'مرشّحين', many: 'مرشّحًا' },
  application: { one: 'طلب واحد', two: 'طلبان', few: 'طلبات', many: 'طلبًا' },
  campaign: { one: 'حملة واحدة', two: 'حملتان', few: 'حملات', many: 'حملة' },
  form: { one: 'استمارة واحدة', two: 'استمارتان', few: 'استمارات', many: 'استمارة' },
  plan: { one: 'خطة واحدة', two: 'خطتان', few: 'خطط', many: 'خطة' },
  site: { one: 'موقع واحد', two: 'موقعان', few: 'مواقع', many: 'موقعًا' },
  unit: { one: 'وحدة واحدة', two: 'وحدتان', few: 'وحدات', many: 'وحدة' },
  position: { one: 'مسمّى واحد', two: 'مسمّيان', few: 'مسمّيات', many: 'مسمًّى' },
  question: { one: 'سؤال واحد', two: 'سؤالان', few: 'أسئلة', many: 'سؤالًا' },
  cycle: { one: 'دورة واحدة', two: 'دورتان', few: 'دورات', many: 'دورة' },
  review: { one: 'تقييم واحد', two: 'تقييمان', few: 'تقييمات', many: 'تقييمًا' },
  goal: { one: 'هدف واحد', two: 'هدفان', few: 'أهداف', many: 'هدفًا' },
  employee: { one: 'موظّف واحد', two: 'موظّفان', few: 'موظّفين', many: 'موظّفًا' },
} as const;

/** الأحرف الأولى من الاسم لصورة رمزية نصية. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return parts[0]![0]! + parts[1]![0]!;
}
